<template>
  <div class="attachment-upload-container">
    <div class="attachment-list">
      <div
        v-for="(attachment, index) in modelValue"
        :key="index"
        :class="['attachment-item', { 'attachment-item-image': isImageAttachment(attachment) }]"
      >
        <!-- 图片附件：显示缩略图，支持预览 -->
        <template v-if="isImageAttachment(attachment)">
          <div class="attachment-image-wrapper">
            <el-image
              :key="`img-${index}-${getImagePreviewUrl(attachment)}`"
              :src="getImagePreviewUrl(attachment)"
              :alt="getAttachmentName(attachment)"
              :preview-src-list="getImagePreviewList"
              :initial-index="getImageIndex(index)"
              fit="cover"
              class="attachment-image-preview"
              :preview-teleported="true"
              :lazy="false"
              crossorigin="anonymous"
              referrerpolicy="no-referrer-when-downgrade"
              @error="handleImageError"
              @load="handleImageLoad"
            >
              <template #error>
                <div class="image-slot">
                  <el-icon><Picture /></el-icon>
                </div>
              </template>
            </el-image>
          </div>
          <div class="attachment-image-info">
            <div class="attachment-image-name-row">
              <div 
                v-if="showImageIcon" 
                :class="['attachment-image-icon', getFileIconClass(attachment)]"
              >
                <span class="file-type-label">{{ getFileTypeLabel(attachment) || 'JPG' }}</span>
              </div>
              <span class="attachment-name" :title="getAttachmentName(attachment)">
                {{ getAttachmentName(attachment) }}
              </span>
              <div class="attachment-actions">
                <el-button
                  v-if="showDownload && attachment.url && !isPendingAttachment(attachment)"
                  type="primary"
                  size="small"
                  :icon="Download"
                  link
                  @click.stop="handleDownload(attachment)"
                >
                  下载
                </el-button>
                <el-button
                  v-if="showActions"
                  type="danger"
                  size="small"
                  :icon="Delete"
                  link
                  @click.stop="handleRemove(index)"
                  class="delete-btn"
                >
                  删除
                </el-button>
              </div>
            </div>
            <div class="attachment-image-meta">
              <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                {{ formatFileSize(getAttachmentSize(attachment)) }}
              </span>
              <el-tag v-if="isPendingAttachment(attachment)" size="small" type="warning" class="pending-tag">
                待上传
              </el-tag>
            </div>
          </div>
        </template>
        
        <!-- 文件附件：显示图标 -->
        <template v-else>
          <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
            <el-icon class="attachment-icon">
              <component :is="getFileIcon(attachment)" />
            </el-icon>
            <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
              {{ getFileTypeLabel(attachment) }}
            </span>
          </div>
          <div class="attachment-file-info">
            <div class="attachment-file-name-row">
              <span class="attachment-name" :title="getAttachmentName(attachment)">
                {{ getAttachmentName(attachment) }}
              </span>
              <div class="attachment-actions">
                <el-button
                  v-if="showDownload && attachment.url && !isPendingAttachment(attachment)"
                  type="primary"
                  size="small"
                  :icon="Download"
                  link
                  @click.stop="handleDownload(attachment)"
                >
                  下载
                </el-button>
                <el-button
                  v-if="showActions"
                  type="danger"
                  size="small"
                  :icon="Delete"
                  link
                  @click.stop="handleRemove(index)"
                  class="delete-btn"
                >
                  删除
                </el-button>
              </div>
            </div>
            <div class="attachment-file-meta">
              <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                {{ formatFileSize(getAttachmentSize(attachment)) }}
              </span>
              <el-tag v-if="isPendingAttachment(attachment)" size="small" type="warning" class="pending-tag">
                待上传
              </el-tag>
            </div>
          </div>
        </template>
      </div>
    </div>
    <el-button
      v-if="showAddButton && !uploading && modelValue.length < maxCount"
      type="primary"
      :icon="Plus"
      @click="handleChoose"
      class="add-attachment-btn"
    >
      + 添加附件
    </el-button>
    <div v-if="uploading" class="upload-progress-container">
      <div class="upload-progress-label">正在上传附件...</div>
      <el-progress 
        :percentage="uploadProgress" 
        :status="uploadProgress === 100 ? 'success' : undefined"
        :stroke-width="8"
      />
      <div class="upload-progress-text">{{ uploadProgress }}%</div>
    </div>
    <div v-if="showTip" class="upload-tip">
      <div class="tip-item">• 最多上传{{ maxCount }}个附件</div>
      <div class="tip-item">• 单个文件不超过20MB，图片不超过2MB</div>
      <div class="tip-item">• 支持格式：PDF、Word、Excel、TXT、JPG、PNG</div>
      <div class="tip-item">• 图片将自动压缩处理</div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, Download, Delete, Plus, Picture, VideoPlay } from '@element-plus/icons-vue'
import { 
  chooseAttachment,
  downloadFileFromServer, 
  formatFileSize, 
  checkDuplicateFile, 
  checkUploadRateLimit,
  batchUploadFiles,
  isImageFile,
  compressImageFile,
  MAX_IMAGE_SIZE
} from '../utils/fileUtils'
import { checkAttachmentQuota, QUOTA_TYPE, setQuotaExceeded } from '../utils/quotaCheck'
import { checkQuota } from '../api/membership'
import { getPreviewUrl } from '../api/file'

const props = defineProps({
  // v-model 绑定的附件列表
  modelValue: {
    type: Array,
    default: () => []
  },
  // 最多上传数量
  maxCount: {
    type: Number,
    default: 10
  },
  // 单次最多上传数量
  maxFilesPerUpload: {
    type: Number,
    default: 5
  },
  // 是否显示提示文字
  showTip: {
    type: Boolean,
    default: true
  },
  // 业务类型（用于上传）
  businessType: {
    type: String,
    default: ''
  },
  // 模块名称（用于上传）
  module: {
    type: String,
    default: ''
  },
  // 是否立即上传（false时只添加文件对象，不实际上传）
  immediate: {
    type: Boolean,
    default: false
  },
  // 是否显示下载按钮（新增/编辑时不显示）
  showDownload: {
    type: Boolean,
    default: false
  },
  // 是否显示操作按钮（删除按钮，新增/编辑时显示，详情页隐藏）
  showActions: {
    type: Boolean,
    default: true
  },
  // 是否显示“添加附件”按钮（详情页一般不需要）
  showAddButton: {
    type: Boolean,
    default: true
  },
  // 是否显示图片类型图标（新增/编辑时不显示，详情页显示）
  showImageIcon: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'remove', 'download'])

const uploading = ref(false)
const uploadProgress = ref(0)
// 缓存文件大小，避免重复请求
const fileSizeCache = ref(new Map())

// 获取附件名称（优先显示原始文件名）
function getAttachmentName(attachment) {
  if (typeof attachment === 'string') {
    // 如果是URL字符串，尝试从后端获取文件名
    return '附件'
  }
  // 优先使用原始文件名
  if (attachment.originalFileName) {
    return attachment.originalFileName
  }
  if (attachment.fileName) {
    return attachment.fileName
  }
  if (attachment.name) {
    return attachment.name
  }
  if (attachment.file && attachment.file.name) {
    return attachment.file.name
  }
  // 如果只有URL，尝试从URL中提取
  if (attachment.url) {
    const urlParts = attachment.url.split('/')
    const fileName = urlParts[urlParts.length - 1].split('?')[0]
    return fileName || '附件'
  }
  return '未知文件'
}

// 通过 HEAD 请求获取文件大小
async function fetchFileSize(url) {
  // 检查缓存
  if (fileSizeCache.value.has(url)) {
    return fileSizeCache.value.get(url)
  }
  
  try {
    // 尝试使用原生 fetch（适用于公开文件或已包含认证信息的 URL）
    const response = await fetch(url, { 
      method: 'HEAD',
      // 如果 URL 包含认证信息，fetch 会自动处理
      credentials: 'include' // 包含 cookies（如果需要）
    })
    
    if (response.ok) {
      const contentLength = response.headers.get('Content-Length')
      if (contentLength) {
        const size = parseInt(contentLength, 10)
        if (size > 0) {
          fileSizeCache.value.set(url, size)
          return size
        }
      }
    }
  } catch (error) {
    // fetch 失败可能是跨域或认证问题，静默处理
    console.debug('通过 fetch 获取文件大小失败，可能需要认证:', error)
  }
  
  // 如果获取失败，返回 0 并缓存，避免重复请求
  fileSizeCache.value.set(url, 0)
  return 0
}

// 获取附件大小
function getAttachmentSize(attachment) {
  if (typeof attachment === 'string') {
    return 0
  }
  // 优先使用保存的大小（如果大于0）
  if (attachment.size !== undefined && attachment.size !== null && attachment.size > 0) {
    return attachment.size
  }
  // 如果保存的大小为0，尝试从file对象获取
  if (attachment.file && attachment.file.size) {
    return attachment.file.size
  }
  // 如果 size 为 0 且有 URL，尝试从缓存获取
  if (attachment.url && fileSizeCache.value.has(attachment.url)) {
    return fileSizeCache.value.get(attachment.url)
  }
  return 0
}

// 异步获取并更新附件大小
async function updateAttachmentSize(attachment, index) {
  if (typeof attachment === 'string' || !attachment.url) {
    return
  }
  
  // 如果已经有有效的大小，不需要获取
  if (attachment.size && attachment.size > 0) {
    return
  }
  
  // 如果 file 对象有大小，也不需要获取
  if (attachment.file && attachment.file.size) {
    return
  }
  
  // 如果已经在缓存中，直接使用
  if (fileSizeCache.value.has(attachment.url)) {
    const cachedSize = fileSizeCache.value.get(attachment.url)
    if (cachedSize > 0) {
      // 更新附件对象的大小
      const updatedAttachments = [...props.modelValue]
      if (index !== undefined && updatedAttachments[index]) {
        updatedAttachments[index] = { ...updatedAttachments[index], size: cachedSize }
        emit('update:modelValue', updatedAttachments)
        emit('change', updatedAttachments)
      }
    }
    return
  }
  
  // 异步获取文件大小（即使当前size为0也要尝试获取）
  const size = await fetchFileSize(attachment.url)
  if (size > 0 && index !== undefined) {
    // 更新附件对象的大小
    const updatedAttachments = [...props.modelValue]
    if (updatedAttachments[index]) {
      updatedAttachments[index] = { ...updatedAttachments[index], size: size }
      emit('update:modelValue', updatedAttachments)
      emit('change', updatedAttachments)
    }
  }
}

// 判断是否为待上传的附件
function isPendingAttachment(attachment) {
  return attachment && typeof attachment === 'object' && attachment.status === 'pending'
}

// 判断是否为图片附件
function isImageAttachment(attachment) {
  if (!attachment) return false
  const fileName = getAttachmentName(attachment)
  return isImageFile(fileName)
}

// 获取图片预览URL（使用代理接口解决COS 403问题）
function getImagePreviewUrl(attachment) {
  let originalUrl = ''
  
  // 如果 attachment 是字符串（URL），直接使用
  if (typeof attachment === 'string') {
    originalUrl = attachment
    console.log('[AttachmentUpload] 附件是字符串类型，原始URL:', originalUrl)
  } else if (attachment && typeof attachment === 'object') {
    // 尝试多种可能的URL属性名
    originalUrl = attachment.url || attachment.fileUrl || attachment.file_url || ''
    console.log('[AttachmentUpload] 附件是对象类型，提取的原始URL:', originalUrl, '附件对象:', attachment)
    
    // 如果是待上传的文件对象，创建预览URL（blob URL不需要代理）
    if (!originalUrl && attachment.file) {
      const blobUrl = URL.createObjectURL(attachment.file)
      console.log('[AttachmentUpload] 创建blob URL:', blobUrl)
      return blobUrl
    }
    
    // 如果没有找到URL，返回空字符串
    if (!originalUrl) {
      console.warn('[AttachmentUpload] 无法获取附件URL，附件对象:', attachment)
      return ''
    }
  } else {
    console.warn('[AttachmentUpload] 附件格式不正确:', attachment)
    return ''
  }
  
  // 如果是blob URL，直接返回（不需要代理）
  if (originalUrl.startsWith('blob:')) {
    console.log('[AttachmentUpload] 是blob URL，直接返回:', originalUrl)
    return originalUrl
  }
  
  // 如果已经是代理URL，直接返回
  if (originalUrl.includes('/files/preview')) {
    console.log('[AttachmentUpload] 已经是代理URL，直接返回:', originalUrl)
    return originalUrl
  }
  
  // 对于COS URL，强制使用代理接口
  if (originalUrl.includes('cos.') || originalUrl.includes('myqcloud.com')) {
    const previewUrl = getPreviewUrl(originalUrl)
    console.log('[AttachmentUpload] COS URL转换为代理URL:', { 
      original: originalUrl, 
      preview: previewUrl
    })
    
    // 最终检查：确保转换成功
    if (previewUrl && (previewUrl.includes('cos.') || previewUrl.includes('myqcloud.com')) && !previewUrl.includes('/files/preview')) {
      console.error('[AttachmentUpload] 严重错误：COS URL转换失败！', { original: originalUrl, preview: previewUrl })
      // 强制转换
      const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
      const forcedProxyUrl = `${cleanBaseUrl}/files/preview?url=${encodeURIComponent(originalUrl)}`
      console.log('[AttachmentUpload] 强制转换为代理URL:', forcedProxyUrl)
      return forcedProxyUrl
    }
    
    return previewUrl
  }
  
  // 非COS URL，直接返回
  console.log('[AttachmentUpload] 非COS URL，直接返回:', originalUrl)
  return originalUrl
}

// 处理图片加载成功
function handleImageLoad(event) {
  const img = event.target
  if (img) {
    console.log('[AttachmentUpload] 图片加载成功:', {
      src: img.src,
      currentSrc: img.currentSrc,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight
    })
    // 检查是否直接使用了COS URL
    if (img.src && (img.src.includes('cos.') || img.src.includes('myqcloud.com')) && !img.src.includes('/files/preview')) {
      console.error('[AttachmentUpload] 警告：图片src直接使用了COS URL，应该使用代理！', img.src)
    }
  }
}

// 处理图片加载错误
function handleImageError(event) {
  // 如果图片加载失败，记录错误信息以便调试
  const img = event.target
  if (img) {
    console.error('[AttachmentUpload] 图片加载失败:', {
      src: img.src,
      currentSrc: img.currentSrc,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight
    })
    // 检查是否直接使用了COS URL
    if (img.src && (img.src.includes('cos.') || img.src.includes('myqcloud.com')) && !img.src.includes('/files/preview')) {
      console.error('[AttachmentUpload] 错误：图片src直接使用了COS URL导致加载失败！', img.src)
    }
    // 不隐藏图片，让el-image显示错误占位符
  }
}

// 获取所有图片的预览列表（用于 el-image 的预览功能）
// 使用computed确保响应式更新，并且所有URL都通过代理
const getImagePreviewList = computed(() => {
  if (!props.modelValue || props.modelValue.length === 0) {
    return []
  }
  
  const imageAttachments = props.modelValue.filter(att => isImageAttachment(att))
  console.log('[AttachmentUpload] 筛选出的图片附件（共', imageAttachments.length, '个）:', imageAttachments)
  
  const previewUrls = imageAttachments
    .map((att, index) => {
      // 先获取原始URL（从附件对象中提取）
      let originalUrl = ''
      if (typeof att === 'string') {
        originalUrl = att
      } else if (att && typeof att === 'object') {
        originalUrl = att.url || att.fileUrl || att.file_url || ''
      }
      
      console.log(`[AttachmentUpload] 预览列表[${index}] 原始URL:`, originalUrl)
      
      // 如果是blob URL，直接返回
      if (originalUrl.startsWith('blob:')) {
        console.log(`[AttachmentUpload] 预览列表[${index}] 是blob URL，直接返回`)
        return originalUrl
      }
      
      // 如果已经是代理URL，直接返回
      if (originalUrl.includes('/files/preview')) {
        console.log(`[AttachmentUpload] 预览列表[${index}] 已经是代理URL，直接返回`)
        return originalUrl
      }
      
      // 对于COS URL，强制转换为代理URL
      if (originalUrl && (originalUrl.includes('cos.') || originalUrl.includes('myqcloud.com'))) {
        const proxyUrl = getPreviewUrl(originalUrl)
        console.log(`[AttachmentUpload] 预览列表[${index}] COS URL转换:`, { original: originalUrl, proxy: proxyUrl })
        
        // 最终检查：确保转换成功
        if (proxyUrl && (proxyUrl.includes('cos.') || proxyUrl.includes('myqcloud.com')) && !proxyUrl.includes('/files/preview')) {
          console.error(`[AttachmentUpload] 预览列表[${index}] 转换失败，强制转换:`, proxyUrl)
          const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
          const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
          const forcedProxyUrl = `${cleanBaseUrl}/files/preview?url=${encodeURIComponent(originalUrl)}`
          console.log(`[AttachmentUpload] 预览列表[${index}] 强制转换后的URL:`, forcedProxyUrl)
          return forcedProxyUrl
        }
        
        return proxyUrl
      }
      
      // 非COS URL，直接返回
      console.log(`[AttachmentUpload] 预览列表[${index}] 非COS URL，直接返回:`, originalUrl)
      return originalUrl
    })
    .filter(url => url && url.length > 0)
  
  // 最终验证：确保预览列表中没有任何COS URL
  const hasCosUrl = previewUrls.some(url => 
    url && !url.startsWith('blob:') && !url.includes('/files/preview') && 
    (url.includes('cos.') || url.includes('myqcloud.com'))
  )
  
  if (hasCosUrl) {
    console.error('[AttachmentUpload] 严重错误：预览列表中仍然包含COS URL！', previewUrls)
  }
  
  console.log('[AttachmentUpload] 预览列表URL（已转换，共', previewUrls.length, '个）:', previewUrls)
  return previewUrls
})

// 获取当前图片在预览列表中的索引
function getImageIndex(currentIndex) {
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = props.modelValue[i]
    if (att && isImageAttachment(att) && !isPendingAttachment(att)) {
      // 检查是否有有效的URL（字符串类型或对象类型的url属性）
      const url = typeof att === 'string' ? att : att.url
      if (url) {
        imageIndex++
      }
    }
  }
  return imageIndex
}

// 获取文件扩展名
function getFileExtension(attachment) {
  const fileName = getAttachmentName(attachment)
  if (!fileName) return ''
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

// 获取文件类型图标
function getFileIcon(attachment) {
  const ext = getFileExtension(attachment)
  // 图片类型
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return Picture
  }
  // 视频类型
  if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) {
    return VideoPlay
  }
  // 其他类型使用文档图标
  return Document
}

// 获取文件类型标签（显示在图标上）
function getFileTypeLabel(attachment) {
  const ext = getFileExtension(attachment)
  const labelMap = {
    'xls': 'XLS',
    'xlsx': 'XLSX',
    'doc': 'DOC',
    'docx': 'DOCX',
    'pdf': 'PDF',
    'txt': 'TXT',
    'ppt': 'PPT',
    'pptx': 'PPTX',
    'zip': 'ZIP',
    'rar': 'RAR',
    '7z': '7Z'
  }
  return labelMap[ext] || ''
}

// 获取文件类型图标样式类
function getFileIconClass(attachment) {
  const ext = getFileExtension(attachment)
  const iconClassMap = {
    // Excel
    'xls': 'icon-excel',
    'xlsx': 'icon-excel',
    // Word
    'doc': 'icon-word',
    'docx': 'icon-word',
    // PDF
    'pdf': 'icon-pdf',
    // 文本
    'txt': 'icon-txt',
    // 图片
    'jpg': 'icon-image',
    'jpeg': 'icon-image',
    'png': 'icon-image',
    'gif': 'icon-image',
    'bmp': 'icon-image',
    'webp': 'icon-image',
    // PPT
    'ppt': 'icon-ppt',
    'pptx': 'icon-ppt',
    // 压缩包
    'zip': 'icon-zip',
    'rar': 'icon-zip',
    '7z': 'icon-zip'
  }
  return iconClassMap[ext] || 'icon-default'
}

// 选择附件
const handleChoose = async () => {
  try {
    // 确保 modelValue 是数组
    const currentAttachments = Array.isArray(props.modelValue) ? props.modelValue : []
    
    // 检查数量限制
    if (currentAttachments.length >= props.maxCount) {
      ElMessage.warning(`最多只能上传${props.maxCount}个附件`)
      return
    }

    // 检查上传限流
    if (!checkUploadRateLimit()) {
      ElMessage.warning('上传过于频繁，请等待2分钟后再试！')
      return
    }

    const result = await chooseAttachment(true)
    
    // 处理返回结果：可能是 FileList 对象（旧版本兼容）或包含 files 和 invalidFiles 的对象
    let files = result
    let invalidFiles = []
    
    if (result && typeof result === 'object' && result.files) {
      // 新版本：返回对象包含 files 和 invalidFiles
      files = result.files
      invalidFiles = result.invalidFiles || []
    } else if (result && result.length !== undefined) {
      // 旧版本兼容：直接是 FileList
      files = result
      invalidFiles = []
    }
    
    // 如果有无效文件，用弹窗提示（停留时间久一点）
    if (invalidFiles && invalidFiles.length > 0) {
      let message = '以下文件不符合要求，已自动过滤：\n\n'
      invalidFiles.forEach((item, index) => {
        message += `${index + 1}. ${item.name}\n   原因：${item.reason}\n`
      })
      message += '\n其他符合要求的文件已加入待上传列表。'
      
      await ElMessageBox.alert(message, '文件大小超限', {
        type: 'warning',
        confirmButtonText: '知道了',
        dangerouslyUseHTMLString: false
      })
    }
    
    // 单次最多上传数量限制
    const fileArray = Array.from(files)
    if (fileArray.length > props.maxFilesPerUpload) {
      await ElMessageBox.alert(`单次最多上传${props.maxFilesPerUpload}个附件`, '提示', {
        type: 'warning',
        confirmButtonText: '知道了'
      })
    }

    // 计算可上传的文件数量
    const remainingSlots = props.maxCount - currentAttachments.length
    const filesToUpload = fileArray.slice(0, Math.min(props.maxFilesPerUpload, remainingSlots))

    if (filesToUpload.length === 0) return

    // 检查附件额度（在选择附件时进行校验）
    // 计算待上传的附件数量（包括当前已有的待上传附件和新选择的文件）
    const pendingCount = currentAttachments.filter(att => 
      att && typeof att === 'object' && att.status === 'pending'
    ).length
    const totalNewFiles = filesToUpload.length
    
    const quotaCheckResult = await checkAttachmentQuota(pendingCount, totalNewFiles, {
      allowLocalUpload: false // 选择时不允许本地操作，直接阻止
    })
    
    if (!quotaCheckResult.allowed) {
      // 额度已超限，不允许选择附件
      return
    }

    const newAttachments = [...currentAttachments]
    const initialCount = newAttachments.length

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i]
      
      console.log(`[AttachmentUpload] 处理文件: ${file.name}, 大小: ${formatFileSize(file.size)}, 是否为图片: ${isImageFile(file.name)}`)

      // 检查重复文件
      const duplicateCheck = await checkDuplicateFile(file, currentAttachments)
      if (!duplicateCheck.shouldContinue) {
        console.log(`[AttachmentUpload] 文件 ${file.name} 重复，跳过`)
        continue
      }

      // 文件大小已经在 chooseFile 中检查过了，这里不需要再次检查
      // 对于图片文件，只有小于2MB的才进行压缩
      let processedFile = file
      if (isImageFile(file.name)) {
        // 确保文件大小小于2MB才压缩
        if (file.size <= MAX_IMAGE_SIZE) {
          try {
            console.log(`[AttachmentUpload] 开始压缩图片: ${file.name}, 原始大小: ${formatFileSize(file.size)}`)
            // 压缩函数内部会根据是否为扫描件自动选择目标大小（扫描件500K，普通图片250K）
            // 如果图片已经小于目标大小，压缩函数会直接返回原文件
            ElMessage.info(`正在压缩图片：${file.name}...`)
            processedFile = await compressImageFile(file)
            console.log(`[AttachmentUpload] 压缩完成: ${file.name}, 压缩后大小: ${formatFileSize(processedFile.size)}`)
          } catch (error) {
            console.warn('[AttachmentUpload] 图片压缩失败，使用原文件:', error)
            // 压缩失败时使用原文件
            processedFile = file
          }
        }
      }

      // 创建附件对象（保存原始文件名）
      const attachmentItem = {
        file: processedFile, // 使用处理后的文件（压缩后的图片或原文件）
        fileName: file.name, // 保持原始文件名
        originalFileName: file.name, // 保存原始文件名
        size: processedFile.size, // 使用处理后的文件大小
        status: 'pending' // 待上传状态
      }

      newAttachments.push(attachmentItem)
    }

    const addedCount = newAttachments.length - initialCount
    if (addedCount > 0) {
      emit('update:modelValue', newAttachments)
      emit('change', newAttachments)
      ElMessage.success(`已添加 ${addedCount} 个附件${props.immediate ? '' : '，将在提交时上传'}`)
    }
  } catch (error) {
    if (error.message !== '用户取消选择' && error.message !== '文件校验失败') {
      console.error('选择附件失败:', error)
      ElMessage.error(error.message || '选择附件失败')
    }
  }
}

// 移除附件
const handleRemove = (index) => {
  const newAttachments = [...props.modelValue]
  const removed = newAttachments.splice(index, 1)[0]
  
  emit('update:modelValue', newAttachments)
  emit('change', newAttachments)
  emit('remove', index, removed)
}

// 下载附件
const handleDownload = async (attachment) => {
  try {
    if (isPendingAttachment(attachment)) {
      ElMessage.warning('文件尚未上传，无法下载')
      return
    }

    const url = typeof attachment === 'string' ? attachment : attachment.url
    const fileName = typeof attachment === 'string' ? undefined : attachment.fileName

    if (!url) {
      ElMessage.warning('文件URL为空')
      return
    }

    await downloadFileFromServer(url, fileName)
    emit('download', attachment)
  } catch (error) {
    console.error('下载附件失败:', error)
  }
}

// 监听附件列表变化，自动获取缺失的文件大小
watch(() => props.modelValue, (newValue) => {
  // 确保 newValue 是数组
  if (!newValue || !Array.isArray(newValue) || newValue.length === 0) {
    return
  }
  
  // 异步获取所有缺失大小的附件
  newValue.forEach((attachment, index) => {
    if (typeof attachment === 'object' && attachment.url) {
      // 如果 size 为 0 或不存在，尝试获取
      if (!attachment.size || attachment.size === 0) {
        updateAttachmentSize(attachment, index)
      }
    }
  })
}, { immediate: true, deep: true })

// 预览器观察器（用于拦截预览时的COS URL）
let previewObserver = null

// 组件挂载时，检查并获取缺失的文件大小
onMounted(() => {
  // 确保 modelValue 是数组
  if (props.modelValue && Array.isArray(props.modelValue) && props.modelValue.length > 0) {
    props.modelValue.forEach((attachment, index) => {
      if (typeof attachment === 'object' && attachment.url) {
        if (!attachment.size || attachment.size === 0) {
          updateAttachmentSize(attachment, index)
        }
      }
    })
  }
  
  // 监听el-image预览器的创建，确保预览时也使用代理URL
  previewObserver = new MutationObserver(() => {
    // 检查是否有el-image-viewer组件被创建
    const imageViewer = document.querySelector('.el-image-viewer__wrapper')
    if (imageViewer) {
      // 查找预览器中的img标签
      const previewImages = imageViewer.querySelectorAll('img')
      previewImages.forEach((img) => {
        const src = img.src || img.getAttribute('src')
        // 如果发现直接使用COS URL，强制替换为代理URL
        if (src && (src.includes('cos.') || src.includes('myqcloud.com')) && !src.includes('/files/preview')) {
          console.error('[AttachmentUpload] 检测到预览器直接使用COS URL，强制替换:', src)
          const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
          const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
          const proxyUrl = `${cleanBaseUrl}/files/preview?url=${encodeURIComponent(src)}`
          console.log('[AttachmentUpload] 替换为代理URL:', proxyUrl)
          img.src = proxyUrl
          img.setAttribute('src', proxyUrl)
        }
      })
    }
  })
  
  // 开始观察DOM变化
  previewObserver.observe(document.body, {
    childList: true,
    subtree: true
  })
})

// 组件卸载时清理观察器
onUnmounted(() => {
  if (previewObserver) {
    previewObserver.disconnect()
    previewObserver = null
  }
})

// 上传所有待上传的附件（供外部调用）
const uploadAll = async () => {
  const pendingAttachments = props.modelValue.filter(a => 
    a && typeof a === 'object' && a.status === 'pending' && a.file
  )
  
  if (pendingAttachments.length === 0) {
    return props.modelValue // 没有待上传的附件，直接返回
  }
  
  uploading.value = true
  uploadProgress.value = 0
  
  try {
    const uploadedAttachments = await batchUploadFiles(
      pendingAttachments,
      props.businessType || 'CONTRACT',
      props.module || '附件管理',
      (progress) => {
        // 确保进度不超过 100%
        uploadProgress.value = Math.min(Math.max(progress, 0), 100)
      }
    )
    
    // 更新附件列表
    const updatedAttachments = [...props.modelValue]
    for (let i = 0; i < updatedAttachments.length; i++) {
      const attachment = updatedAttachments[i]
      if (attachment && typeof attachment === 'object' && attachment.status === 'pending' && attachment.file) {
        // 通过原始文件名或当前文件名匹配
        const uploaded = uploadedAttachments.find(u => 
          u.fileName === attachment.fileName || 
          u.fileName === attachment.originalFileName ||
          (attachment.file && u.fileName === attachment.file.name)
        )
        if (uploaded) {
          let fileUrl = uploaded.url
          if (!fileUrl.startsWith('http')) {
            const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
            fileUrl = fileUrl.startsWith('/') ? baseURL + fileUrl : `${baseURL}/${fileUrl}`
          }
          // 保存原始文件名和大小
          updatedAttachments[i] = {
            url: fileUrl,
            fileName: uploaded.fileName,
            originalFileName: uploaded.originalFileName || uploaded.fileName || attachment.originalFileName || attachment.fileName || attachment.file?.name,
            size: attachment.size || attachment.file?.size || uploaded.size || 0
          }
        }
      }
    }
    
    emit('update:modelValue', updatedAttachments)
    emit('change', updatedAttachments)
    
    // 上传成功后，检查额度是否已超限，如果超限则记录状态
    try {
      const quotaResult = await checkQuota(QUOTA_TYPE.ATTACHMENT)
      if (quotaResult && quotaResult.data) {
        const quotaItem = quotaResult.data
        // 如果额度已超限，记录状态（下次选择附件时直接不允许）
        if (quotaItem.limit !== null && quotaItem.used !== null && quotaItem.exceeded) {
          // 使用工具函数记录状态
          setQuotaExceeded(QUOTA_TYPE.ATTACHMENT)
        }
      }
    } catch (error) {
      // 额度检查失败不影响上传结果
      console.warn('上传后检查附件额度失败:', error)
    }
    
    if (uploading.value) {
      uploading.value = false
      uploadProgress.value = 0
    }
    
    return updatedAttachments
  } catch (error) {
    if (uploading.value) {
      uploading.value = false
      uploadProgress.value = 0
    }
    throw error
  }
}

// 暴露方法供外部调用
defineExpose({
  uploadAll
})
</script>

<style scoped>
.attachment-upload-container {
  width: 100%;
}

.attachment-list {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.attachment-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  background-color: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  width: calc(50% - 6px); /* 每行显示2个，减去gap的一半 */
  box-sizing: border-box;
  min-height: 80px;
}

.attachment-item:hover {
  background-color: #fafbfc;
  border-color: #c0c4cc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.attachment-icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-right: 14px;
  flex-shrink: 0;
  border-radius: 8px;
  background-color: #f5f7fa;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.attachment-icon {
  font-size: 24px;
}

.file-type-label {
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Excel 图标样式 */
.icon-excel {
  background: linear-gradient(135deg, #1d6f42 0%, #2a8f56 100%);
}

.icon-excel .attachment-icon {
  color: #fff;
}

/* Word 图标样式 */
.icon-word {
  background: linear-gradient(135deg, #2b579a 0%, #3d6fb8 100%);
}

.icon-word .attachment-icon {
  color: #fff;
}

/* PDF 图标样式 */
.icon-pdf {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-pdf .attachment-icon {
  color: #fff;
}

/* 文本图标样式 */
.icon-txt {
  background: linear-gradient(135deg, #666666 0%, #808080 100%);
}

.icon-txt .attachment-icon {
  color: #fff;
}

/* 图片图标样式 */
.icon-image {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
}

.icon-image .attachment-icon {
  color: #fff;
}

/* PPT 图标样式 */
.icon-ppt {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-ppt .attachment-icon {
  color: #fff;
}

/* 压缩包图标样式 */
.icon-zip {
  background: linear-gradient(135deg, #ff9800 0%, #ffb340 100%);
}

.icon-zip .attachment-icon {
  color: #fff;
}

/* 默认图标样式 */
.icon-default {
  background: #f5f7fa;
}

.icon-default .attachment-icon {
  color: #909399;
}

.attachment-file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0; /* 允许flex子项收缩 */
}

.attachment-file-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.attachment-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #303133;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  min-width: 0; /* 允许文本截断 */
}

.attachment-file-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.attachment-size {
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
  font-weight: 400;
}

.pending-tag {
  flex-shrink: 0;
}

.attachment-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.upload-progress-container {
  margin: 12px 0;
  padding: 16px;
  background-color: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.upload-progress-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
  font-weight: 500;
}

.upload-progress-text {
  font-size: 12px;
  color: #909399;
  text-align: right;
  margin-top: 8px;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 12px;
  line-height: 1.8;
}

.upload-tip .tip-item {
  margin-bottom: 2px;
}

/* 图片附件样式 */
.attachment-item-image {
  border: 1px solid #e4e7ed;
  background-color: #ffffff;
  padding: 14px;
  width: calc(50% - 6px); /* 每行显示2个，减去gap的一半 */
  box-sizing: border-box;
}

.attachment-item-image:hover {
  border-color: #c0c4cc;
}

.attachment-image-wrapper {
  width: 88px;
  height: 88px;
  margin-right: 14px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.attachment-image-wrapper:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}

.attachment-image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.attachment-image-preview:hover {
  transform: scale(1.05);
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 20px;
}

.attachment-image-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
  min-width: 0; /* 允许flex子项收缩 */
}

.attachment-image-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.attachment-image-icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.attachment-image-icon .file-type-label {
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.attachment-image-name-row .attachment-name {
  flex: 1;
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0; /* 允许文本截断 */
}

.attachment-image-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.attachment-image-meta .attachment-size {
  font-size: 12px;
  color: #909399;
}

.attachment-image-meta .pending-tag {
  flex-shrink: 0;
}

.delete-btn {
  width: 20px;
  height: 20px;
  padding: 0;
  min-height: 20px;
  border-radius: 50%;
  background-color: #f56c6c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background-color: #f78989;
}

.delete-btn .el-icon {
  font-size: 12px;
}

.add-attachment-btn {
  margin-top: 8px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.add-attachment-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}
</style>

